import axios from "axios";
import * as session from "../modules/session/actions";
import * as notifications from "../modules/notifications/actions";

function defaultOnCanceled(dispatch, err) {
    throw err;
}

function defaultOnNotAuthenticated(dispatch, err) {
    dispatch(session.resetAuthToken(err));
    throw err;
}

function defaultOnNotAuthorized(dispatch, err) {
    return defaultOnError(dispatch, err);
}

function defaultOnError(dispatch, err) {
    const response = err.response;
    if (!response) {
        dispatch(
            notifications.notifyError(
                `A request to the Wevo servers failed`,
                "exclamation-triangle"
            )
        );
        throw err;
    }

    const message = response.data && response.data.message;
    if (message) {
        dispatch(
            notifications.notifyError(
                `A request to the Wevo servers failed: ${message}`,
                "exclamation-triangle"
            )
        );
        throw err;
    }

    const statusText = response.statusText;
    if (statusText) {
        dispatch(
            notifications.notifyError(
                `A request to the Wevo servers failed: ${statusText}`,
                "exclamation-triangle"
            )
        );
        throw err;
    }

    dispatch(
        notifications.notifyError(
            `A request to the Wevo servers failed`,
            "exclamation-triangle"
        )
    );
    throw err;
}

export const defaultErrorHandlers = {
    onNotAuthenticated: defaultOnNotAuthenticated,
    onNotAuthorized: defaultOnNotAuthenticated,
    onError: defaultOnError
};

function noOpErrorHandler(dispatch, err) {
    throw err;
}

export const noOpErrorHandlers = {
    onNotAuthenticated: noOpErrorHandler,
    onNotAuthorized: noOpErrorHandler,
    onError: noOpErrorHandler
};

/**
 * Issues an HTTP request and handles errors.
 *
 * @param dispatch the Redux action dispatch function.
 *
 * @param reqFn `(axios) => Promise` the function that starts the request.
 *
 * @param onCanceled `(dispatch, err) => any` the function to call if the request is canceled. The default function
 * simply re-throws the error.
 *
 * @param onNotAuthenticated `(dispatch, err) => any` the function to call if the request returns a 401 status code. The
 * default function clears out the existing auth token, which usually forces the app to render the login screen. It also
 * re-throws the error.
 *
 * @param onNotAuthorized `(dispatch, err) => any` the function to call if the request returns a 403 status code. The
 * default function simply re-throws the error.
 *
 * @param onError `(dispatch, err) => any` the function to call if the request returns any other error status code. The
 * default function dispatches a `notifyError` action and re-throws the error.
 *
 * @return the Promise returned by `reqFn` with a `catch` handler for the errors mentioned above.
 */
export function requestWithAuth(
    dispatch,
    reqFn,
    {
        onCanceled,
        onNotAuthenticated,
        onNotAuthorized,
        onError
    } = defaultErrorHandlers
) {
    return reqFn(axios).catch(err => {
        if (axios.isCancel(err)) {
            const fn = onCanceled || defaultOnCanceled;
            return fn(dispatch, err);
        }

        const status = err.response && err.response.status;
        if (status === 401) {
            const fn = onNotAuthenticated || defaultOnNotAuthenticated;
            return fn(dispatch, err);
        }

        if (status === 403) {
            const fn = onNotAuthorized || defaultOnNotAuthorized;
            return fn(dispatch, err);
        }

        const fn = onError || defaultOnError;
        return fn(dispatch, err);
    });
}

/**
 * Issues an HTTP request and handles errors. Same as above but doesn't do anything on
 * 401s or 403s
 *
 * @param dispatch the Redux action dispatch function.
 *
 * @param reqFn `(axios) => Promise` the function that starts the request.
 *
 * @param onCanceled `(dispatch, err) => any` the function to call if the request is canceled. The default function
 * simply re-throws the error.
 *
 * @param onError `(dispatch, err) => any` the function to call if the request returns any other error status code. The
 * default function dispatches a `notifyError` action and re-throws the error.
 *
 * @return the Promise returned by `reqFn` with a `catch` handler for the errors mentioned above.
 */

export function requestWithoutAuth(
    dispatch,
    reqFn,
    { onCanceled, onError } = defaultErrorHandlers
) {
    return reqFn(axios).catch(err => {
        if (axios.isCancel(err)) {
            const fn = onCanceled || defaultOnCanceled;
            return fn(dispatch, err);
        }

        const fn = onError || defaultOnError;
        return fn(dispatch, err);
    });
}



// WEBPACK FOOTER //
// ./src/utils/request.js